import { useState, useEffect, useCallback } from 'react';
import {
  BodyText,
  Button,
  Card,
  Badge,
  Input,
  Select,
  DataGrid,
  Tooltip,
} from '@aaimplatform/ui';
import type {
  ColumnDef,
  PaginationState,
} from '@aaimplatform/ui';
import {
  Download as ExportIcon,
  RefreshCw as RefreshIcon,
  Circle as LiveIcon,
} from 'lucide-react';
import { auditLogsApi } from '../api/client';
import type { AuditLogEntryResponse, AuditActionType } from '../api/client';

/**
 * Action categories for the filter dropdown.
 * Drawn from the OpenAPI AuditActionType enum, grouped into
 * the most common categories visible in the admin portal.
 */
const ACTION_FILTER_OPTIONS: { value: AuditActionType; label: string }[] = [
  { value: 'USER_LOGIN', label: 'User Login' },
  { value: 'USER_LOGOUT', label: 'User Logout' },
  { value: 'DATA_ACCESS', label: 'Data Access' },
  { value: 'DATA_MODIFICATION', label: 'Data Modification' },
  { value: 'DATA_EXPORT', label: 'Data Export' },
  { value: 'DATA_DELETION', label: 'Data Deletion' },
  { value: 'CONFIGURATION_CHANGE', label: 'Configuration Change' },
  { value: 'PERMISSION_CHANGE', label: 'Permission Change' },
  { value: 'COMPLIANCE_CHECK', label: 'Compliance Check' },
  { value: 'COMPLIANCE_VIOLATION', label: 'Compliance Violation' },
  { value: 'CREATE_TENANT', label: 'Create Tenant' },
  { value: 'UPDATE_TENANT', label: 'Update Tenant' },
  { value: 'KEY_ROTATION', label: 'Key Rotation' },
  { value: 'ENCRYPT_PII', label: 'Encrypt PII' },
  { value: 'DECRYPT_PII', label: 'Decrypt PII' },
];

/**
 * Resource types for the filter dropdown.
 * Values correspond to the resourceType enum from audit-trail.openapi.yaml.
 */
const RESOURCE_TYPE_OPTIONS = [
  'session',
  'tenant',
  'user_account',
  'configuration',
  'loan_application',
  'financial_account',
  'report',
  'compliance_rule',
  'api_key',
  'consumer_identity',
  'encryption_key',
  'document',
];

/**
 * Map action types to Badge color variants for visual distinction.
 */
function getActionVariant(action: string): 'neutral' | 'success' | 'info' | 'error' | 'warning' {
  if (action.includes('LOGIN')) return 'success';
  if (action.includes('LOGOUT')) return 'neutral';
  if (action.includes('CREATE') || action.includes('MODIFICATION')) return 'info';
  if (action.includes('DELETE') || action.includes('VIOLATION')) return 'error';
  if (action.includes('CHANGE') || action.includes('ROTATION')) return 'warning';
  if (action.includes('EXPORT') || action.includes('ACCESS')) return 'neutral';
  if (action.includes('ENCRYPT') || action.includes('DECRYPT')) return 'info';
  if (action.includes('COMPLIANCE')) return 'warning';
  return 'neutral';
}

/**
 * Derive a risk indicator from the action type for display purposes.
 */
function getRiskLevel(action: string): { label: string; variant: 'neutral' | 'warning' | 'error' } {
  if (action.includes('DELETE') || action.includes('VIOLATION') || action.includes('FAILED')) {
    return { label: 'CRITICAL', variant: 'error' };
  }
  if (action.includes('CHANGE') || action.includes('ROTATION') || action.includes('PERMISSION')) {
    return { label: 'WARNING', variant: 'warning' };
  }
  return { label: 'INFO', variant: 'neutral' };
}

/**
 * Format an action type string for display.
 */
function formatAction(action: string): string {
  return action.replace(/_/g, ' ');
}

/**
 * Format a resource type string for display (snake_case to Title Case).
 */
function formatResourceType(resourceType: string): string {
  return resourceType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Polling interval for live mode (milliseconds).
 */
const LIVE_POLL_INTERVAL = 10000;

// ---------------------------------------------------------------------------
// Column Definitions
// ---------------------------------------------------------------------------

const COLUMNS: ColumnDef<AuditLogEntryResponse, unknown>[] = [
  {
    accessorKey: 'timestamp',
    header: 'Timestamp',
    cell: ({ getValue }) => {
      const ts = new Date(getValue() as string);
      return (
        <div>
          <span className="text-sm font-sans text-primary leading-normal">
            {ts.toLocaleDateString()}
          </span>
          <div className="text-xs font-sans text-tertiary leading-normal">
            {ts.toLocaleTimeString()}
          </div>
        </div>
      );
    },
    size: 150,
  },
  {
    accessorKey: 'userId',
    header: 'User',
    cell: ({ row }) => (
      <div>
        <span className="text-sm font-sans text-primary leading-normal">
          {row.original.userId}
        </span>
        <div className="text-xs font-sans text-tertiary leading-normal">
          {row.original.tenantId.slice(0, 8)}...
        </div>
      </div>
    ),
    size: 160,
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ getValue }) => {
      const action = getValue() as string;
      return (
        <Badge variant={getActionVariant(action)} size="sm">
          {formatAction(action)}
        </Badge>
      );
    },
    size: 170,
  },
  {
    accessorKey: 'resourceType',
    header: 'Resource Type',
    cell: ({ getValue }) => (
      <Badge variant="neutral" size="sm">
        {formatResourceType(getValue() as string)}
      </Badge>
    ),
    size: 140,
  },
  {
    accessorKey: 'resourceId',
    header: 'Resource ID',
    cell: ({ getValue }) => {
      const value = getValue() as string | null | undefined;
      if (!value) {
        return (
          <Tooltip content="This event type does not target a specific resource">
            <span className="font-mono text-xs text-quaternary leading-normal cursor-help">
              N/A
            </span>
          </Tooltip>
        );
      }
      return (
        <span className="font-mono text-xs text-primary leading-normal">
          {value}
        </span>
      );
    },
    size: 140,
  },
  {
    accessorKey: 'ipAddress',
    header: 'IP Address',
    cell: ({ getValue }) => {
      // A system-originated row (scheduled export, termination snapshot) has
      // no request origin and carries a null IP; it is never shown as an
      // address.
      const value = getValue() as AuditLogEntryResponse['ipAddress'];
      if (value === null || value === undefined) {
        return (
          <span className="text-xs font-sans text-quaternary leading-normal">
            Not recorded
          </span>
        );
      }
      return (
        <span className="font-mono text-xs text-primary leading-normal">
          {value}
        </span>
      );
    },
    size: 130,
  },
  {
    id: 'risk',
    header: 'Risk',
    accessorFn: (row) => row.action,
    cell: ({ row }) => {
      const risk = getRiskLevel(row.original.action);
      return (
        <Badge variant={risk.variant} size="sm">
          {risk.label}
        </Badge>
      );
    },
    size: 100,
  },
];

// ---------------------------------------------------------------------------
// Expanded Row Detail
// ---------------------------------------------------------------------------

function AuditLogDetail({ row }: { row: { original: AuditLogEntryResponse } }) {
  const log = row.original;
  return (
    <div className="p-4 bg-recessed">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <div>
          <BodyText variant="ui" className="font-semibold text-xs text-tertiary mb-1">User Agent</BodyText>
          <BodyText variant="ui" className="text-xs font-mono">{log.userAgent}</BodyText>
        </div>
        <div>
          <BodyText variant="ui" className="font-semibold text-xs text-tertiary mb-1">Integrity Hash</BodyText>
          <BodyText variant="ui" className="text-xs font-mono">{log.integrityHash}</BodyText>
        </div>
      </div>
      <BodyText variant="ui" className="font-semibold mb-2">Details</BodyText>
      <pre className="bg-elevated p-3 rounded font-mono text-xs overflow-auto">
        {JSON.stringify(log.details, null, 2)}
      </pre>
      {log.metadata && Object.keys(log.metadata).length > 0 && (
        <>
          <BodyText variant="ui" className="font-semibold mb-2 mt-3">Metadata</BodyText>
          <pre className="bg-elevated p-3 rounded font-mono text-xs overflow-auto">
            {JSON.stringify(log.metadata, null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AuditLogsPage Component
// ---------------------------------------------------------------------------

/**
 * Audit Logs Page -- Modernized with DataGrid and server-side pagination.
 *
 * Uses the UI Kit DataGrid component with server-side pagination via the
 * `pagination` prop. Row expansion shows detailed log metadata. Filter
 * controls remain as dedicated inputs for date range, action type,
 * resource type, and user ID since these drive server-side queries.
 */
export function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLogEntryResponse[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLive, setIsLive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Filters
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [resourceFilter, setResourceFilter] = useState<string>('');
  const [actorFilter, setActorFilter] = useState<string>('');

  /**
   * Fetch audit logs from the API with current filter state.
   */
  const fetchLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const result = await auditLogsApi.list({
        action: (actionFilter || undefined) as AuditActionType | undefined,
        resourceType: resourceFilter || undefined,
        userId: actorFilter || undefined,
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sortBy: 'timestamp',
        sortOrder: 'desc',
      });

      setLogs(result.data);
      setTotalItems(result.pagination.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch audit logs';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [actionFilter, resourceFilter, actorFilter, startDate, endDate, rowsPerPage, page]);

  // Fetch on mount and when filters/pagination change
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Live polling
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => {
      fetchLogs();
    }, LIVE_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isLive, fetchLogs]);

  const handleExport = async () => {
    try {
      const blob = await auditLogsApi.exportCsv({
        action: (actionFilter || undefined) as AuditActionType | undefined,
        resourceType: resourceFilter || undefined,
        userId: actorFilter || undefined,
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
        sortBy: 'timestamp',
        sortOrder: 'desc',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export audit logs';
      setErrorMessage(message);
    }
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setActionFilter('');
    setResourceFilter('');
    setActorFilter('');
    setPage(0);
  };

  // Server-side pagination state for DataGrid
  const paginationState: PaginationState = {
    page,
    pageSize: rowsPerPage,
    total: totalItems,
    onPageChange: setPage,
    onPageSizeChange: (size) => {
      setRowsPerPage(size);
      setPage(0);
    },
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <BodyText variant="ui" className="text-tertiary">
            Track all system activities and configuration changes
          </BodyText>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setIsLive(!isLive)}
            className="inline-flex items-center gap-1 px-3 py-1 rounded text-sm border border-recessed hover:bg-recessed transition-colors"
          >
            <LiveIcon className={isLive ? 'text-growth' : 'text-tertiary'} size={12} />
            <span className={isLive ? 'text-growth' : 'text-secondary'}>
              {isLive ? 'Live' : 'Paused'}
            </span>
          </button>
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
            <RefreshIcon className="mr-2" size={20} />
            Refresh
          </Button>
          <Button variant="primary" onClick={handleExport} disabled={logs.length === 0}>
            <ExportIcon className="mr-2" size={20} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Server-side Filters */}
      <Card className="p-4 mb-6">
        <BodyText variant="ui" className="font-semibold mb-3">Filters</BodyText>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Action Type</label>
            <Select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              className="w-full"
            >
              <option value="">All</option>
              {ACTION_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Resource Type</label>
            <Select
              value={resourceFilter}
              onChange={(e) => { setResourceFilter(e.target.value); setPage(0); }}
              className="w-full"
            >
              <option value="">All</option>
              {RESOURCE_TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>{formatResourceType(type)}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-1">User ID</label>
            <Input
              value={actorFilter}
              onChange={(e) => { setActorFilter(e.target.value); setPage(0); }}
              placeholder="Filter by user ID"
              className="w-full"
            />
          </div>
        </div>
        <div className="mt-3">
          <Button variant="ghost" onClick={clearFilters}>Clear Filters</Button>
        </div>
      </Card>

      {/* DataGrid with server-side pagination and row expansion */}
      <DataGrid<AuditLogEntryResponse>
        data={logs}
        columns={COLUMNS}
        pagination={paginationState}
        enableExpanding
        renderSubComponent={AuditLogDetail}
        enableColumnResize
        isLoading={isLoading && logs.length === 0}
        errorMessage={errorMessage ?? undefined}
        hoverable
        striped
        density="compact"
        getRowId={(row) => row.id}
        emptyMessage="No audit logs found matching the current filters."
        className="w-full shadow-sm"
      />
    </div>
  );
}
